'use client';

/**
 * `useSubmitTransaction` — frontend binding for the SDK transaction engine.
 *
 * The 3,663-LOC transaction-submission engine was relocated into
 * `@morfi/backend-sdk-v2` as part of spec 009 FILE 14 Group E (auth-5). The
 * SDK owns the engine; the engine is vendor-neutral. This thin wrapper
 * threads the host-specific bindings the SDK forbids in its core — Statsig
 * feature gates, the per-network RPC endpoint resolver, the Better Stack
 * logger, the typed-analytics event emitter — into the SDK's
 * `useSubmitTransaction` hook.
 *
 * Application code calls `const submitTx = useSubmitTransaction()` at render
 * and `await submitTx(buildResult, options)` inside its mutation/handler.
 */
import { c as _c } from "react/compiler-runtime";
import { type BlockhashResolverMode, isBlockhashResolverMode, type SubmitTransactionFn, type TxConnectionConfig, type TxEngineEmitEvent, type TxEngineFlags, useSubmitTransaction as useSdkSubmitTransaction } from '@morfi/backend-sdk-v2/client';
// `onEvent` is strongly typed; the engine emits the same `tx_*` event names
// the frontend's typed event registry already defines, via a generic
// `(name, payload)` shape — bridged by `emitEvent` below.
import { onEvent } from '@/events';
import { getConfig } from '@/lib/analytics';
import { env } from '@/lib/config/env';
import { makeInstrumentedHeliusFetch } from '@/lib/dev/helius-instrument';
import { useImpersonationStore } from '@/lib/impersonation';
import { getNetworkConfig, type Network } from '@/lib/network';
import { getActiveNetwork } from '@/lib/network/active';
import { logger } from '@/lib/telemetry/logger';
import { recordAttemptSignature } from '@/stores/trade-attempt-journal';
import { deriveWsEndpoint } from './connection';
const IS_DEV: boolean = process.env.NODE_ENV === 'development';

/**
 * Bridge the engine's generic `(name, payload)` emitter onto the frontend's
 * registry-typed `onEvent`. The engine emits the registered `tx_*` event
 * names; the registry-key + payload narrowing happens here at the boundary.
 */
const emitEvent: TxEngineEmitEvent = (name: string, payload: Record<string, unknown>): void => {
  if (typeof payload.traceId === 'string' && typeof payload.signature === 'string') {
    recordAttemptSignature(payload.traceId, payload.signature);
  }
  onEvent(name as Parameters<typeof onEvent>[0], payload as never);
};

/**
 * Resolve the live Solana connection config for the active network. Reads
 * `@/lib/network` + env — re-evaluated on every submission so a network
 * toggle is picked up.
 */
export function resolveConnectionConfig(network: Network = getActiveNetwork()): TxConnectionConfig {
  const config = getNetworkConfig(network);
  const instrumentFetch = process.env.NODE_ENV !== 'production' ? makeInstrumentedHeliusFetch() : undefined;
  return {
    network,
    rpcUrl: config.rpcUrl,
    wsUrl: deriveWsEndpoint(config.rpcUrl),
    caip2: network === 'mainnet' ? 'solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp' : 'solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1wcaWoxPkrZBG',
    instrumentFetch
  };
}

/** Resolve the live Statsig feature-flag snapshot for the engine. */
function resolveFlags(): TxEngineFlags {
  const maxRetriesConfig = getConfig('tx_engine_max_retries');
  const timeoutConfig = getConfig('tx_engine_confirmation_timeout_ms');
  const blockhashModeConfig = getConfig('tx_engine_blockhash_resolver_mode');
  return {
    maxRetries: typeof maxRetriesConfig.value === 'number' ? maxRetriesConfig.value : undefined,
    confirmationTimeoutMs: typeof timeoutConfig.value === 'number' ? timeoutConfig.value : undefined,
    blockhashResolverMode: resolveBlockhashResolverMode(typeof blockhashModeConfig.value === 'string' ? blockhashModeConfig.value : undefined)
  };
}
function resolveBlockhashResolverMode(value: string | undefined): BlockhashResolverMode {
  const raw = value ?? env.NEXT_PUBLIC_MORFI_BLOCKHASH_RESOLVER_MODE;
  return isBlockhashResolverMode(raw) ? raw : 'stream';
}

/**
 * Returns an imperative `submitTransaction` function bound to the SDK
 * transaction engine with the frontend's Statsig + network config injected.
 */
export function useSubmitTransaction() {
  const $ = _c(2);
  const impersonationActive = useImpersonationStore(_temp);
  let t0;
  if ($[0] !== impersonationActive) {
    t0 = {
      logger,
      emitEvent,
      resolveConnectionConfig: _temp2,
      resolveFlags,
      impersonationActive,
      isDev: IS_DEV
    };
    $[0] = impersonationActive;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const deps = t0;
  return useSdkSubmitTransaction(deps);
}
function _temp2() {
  return resolveConnectionConfig();
}
function _temp(s) {
  return s.isActive;
}
export type { SubmitTransactionFn };